import { FC, useEffect, useState } from 'react';
import {
  DatePicker,
  DatePickerInput,
  Dropdown,
  TextInput,
} from '@carbon/react';
import type { ImportedWellInput } from './types';
import styles from './structuring-module.module.scss';

// Shared "Create Imported Well" form for Add Well + Change Match. Mirrors the
// mock `CreateNewWellForm`; emits the snake_case `ImportedWellInput` the backend
// expects. Valid once a well name is present (API number optional for a
// imported well, per the spec — `api_number` nullable).

const WELL_STATUS_OPTIONS = ['Active', 'Pending', 'Shut In', 'Inactive'];
const HOLE_DIRECTION_OPTIONS = ['Horizontal', 'Vertical', 'Directional'];

interface FormState {
  wellName: string;
  apiNumber: string;
  formation: string;
  wellStatus: string;
  holeDirection: string;
  currentOperator: string;
  permitDate: string;
  spudDate: string;
  firstProductionDate: string;
}

const EMPTY: FormState = {
  wellName: '',
  apiNumber: '',
  formation: '',
  wellStatus: '',
  holeDirection: '',
  currentOperator: '',
  permitDate: '',
  spudDate: '',
  firstProductionDate: '',
};

const fmtDate = (d: Date): string =>
  `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(
    2,
    '0'
  )}/${d.getFullYear()}`;

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

interface Props {
  onChange: (data: ImportedWellInput) => void;
  onValidityChange: (valid: boolean) => void;
}

export const ImportedWellForm: FC<Props> = ({ onChange, onValidityChange }) => {
  const [form, setForm] = useState<FormState>(EMPTY);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    onValidityChange(form.wellName.trim() !== '');
    onChange({
      well_name: form.wellName.trim(),
      current_operator: blank(form.currentOperator),
      api_number: blank(form.apiNumber),
      formation: blank(form.formation),
      well_status: blank(form.wellStatus),
      hole_direction: blank(form.holeDirection),
      permit_date: blank(form.permitDate),
      spud_date: blank(form.spudDate),
      first_production_date: blank(form.firstProductionDate),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <div className={styles.importedWellGrid}>
      <div className={styles.importedWellColumn}>
        <TextInput
          id="gw-name"
          labelText="Well Name"
          placeholder="e.g. Commerce Comet 221H"
          size="sm"
          value={form.wellName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setField('wellName', e.target.value)
          }
        />
        <TextInput
          id="gw-api"
          labelText="API Number"
          placeholder="e.g. 30-025-40221"
          size="sm"
          value={form.apiNumber}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setField('apiNumber', e.target.value)
          }
        />
        <TextInput
          id="gw-formation"
          labelText="Formation"
          placeholder="e.g. Avalon"
          size="sm"
          value={form.formation}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setField('formation', e.target.value)
          }
        />
        <Dropdown
          id="gw-well-status"
          titleText="Well Status"
          label="Choose a status"
          size="sm"
          items={WELL_STATUS_OPTIONS}
          selectedItem={form.wellStatus || null}
          onChange={({ selectedItem }: { selectedItem: string | null }) =>
            setField('wellStatus', selectedItem ?? '')
          }
        />
        <Dropdown
          id="gw-hole-direction"
          titleText="Hole Direction"
          label="Choose a direction"
          size="sm"
          items={HOLE_DIRECTION_OPTIONS}
          selectedItem={form.holeDirection || null}
          onChange={({ selectedItem }: { selectedItem: string | null }) =>
            setField('holeDirection', selectedItem ?? '')
          }
        />
      </div>

      <div className={styles.importedWellColumn}>
        <TextInput
          id="gw-operator"
          labelText="Current Operator"
          placeholder="e.g. Permian Resources Operating LLC"
          size="sm"
          value={form.currentOperator}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setField('currentOperator', e.target.value)
          }
        />
        <DatePicker
          datePickerType="single"
          value={form.permitDate}
          onChange={(dates: Date[]) => {
            if (dates[0]) setField('permitDate', fmtDate(dates[0]));
          }}
        >
          <DatePickerInput
            id="gw-permit-date"
            labelText="Permit Date"
            placeholder="MM/DD/YYYY"
            size="sm"
          />
        </DatePicker>
        <DatePicker
          datePickerType="single"
          value={form.spudDate}
          onChange={(dates: Date[]) => {
            if (dates[0]) setField('spudDate', fmtDate(dates[0]));
          }}
        >
          <DatePickerInput
            id="gw-spud-date"
            labelText="Spud Date"
            placeholder="MM/DD/YYYY"
            size="sm"
          />
        </DatePicker>
        <DatePicker
          datePickerType="single"
          value={form.firstProductionDate}
          onChange={(dates: Date[]) => {
            if (dates[0]) setField('firstProductionDate', fmtDate(dates[0]));
          }}
        >
          <DatePickerInput
            id="gw-first-production-date"
            labelText="First Production Date"
            placeholder="MM/DD/YYYY"
            size="sm"
          />
        </DatePicker>
      </div>
    </div>
  );
};
